import type { AxiosError } from 'axios';
import { type UploadedFile, createUploadedFile } from 'models';
import { useMutation } from 'query';
import type { UploadProgressEvent } from 'ui';
// TODO: break circular dependency
import { handleAxiosError } from 'ui/axios-error-handler';
import { uploadFile } from '../../services';

const defaultMutateOnError = <TError extends AxiosError | Error = AxiosError>(
  error: TError,
  { errorMessage }: { errorMessage?: string },
) => {
  handleAxiosError(error, errorMessage);
};

type UploadFileVariables =
  | { errorMessage?: string; file: Blob | File | string; onProgress?: (progressEvent: UploadProgressEvent) => void }
  | { errorMessage?: string; name: string; url: string };

const createUploadFileBody = (variables: UploadFileVariables) => {
  if ('file' in variables) {
    const formData = new FormData();
    formData.append('file', variables.file);
    return formData;
  }

  return { name: variables.name, url: variables.url };
};

export const useUploadFileMutation = () =>
  useMutation<UploadedFile, UploadFileVariables>({
    defaultMutateOnError,
    async mutationFn(variables) {
      const body = createUploadFileBody(variables);

      const response = await uploadFile({
        body,
        config: {
          onUploadProgress:
            'file' in variables
              ? ({ loaded, total, ...rest }) => {
                  variables.onProgress?.({
                    loaded,
                    percent: Math.floor((loaded / (total ?? loaded)) * 100),
                    total,
                    ...rest,
                  });
                }
              : undefined,
          params: {},
        },
      });

      return createUploadedFile(response.data);
    },
  });
